import { useRef } from 'react';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { StreamingIndicator } from './StreamingIndicator';
import { RetryButton } from './RetryButton';
import { useChat } from '../../hooks/useChat';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import './ChatInterface.css';

/**
 * Main chat orchestrator component.
 * Composes MessageList, MessageInput, StreamingIndicator, and RetryButton.
 * Wires useChat, useAutoScroll, and useNetworkStatus hooks.
 *
 * Requirements: 1.1, 2.2, 7.2, 7.4, 10.5
 */
export function ChatInterface() {
  const messageListRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    isLoading,
    isStreaming,
    error,
    draftMessage,
    submitMessage,
    retry,
  } = useChat();

  const { showScrollButton, scrollToBottom } = useAutoScroll(
    messageListRef,
    isStreaming
  );

  const { isOnline } = useNetworkStatus();

  const inputDisabled = isLoading || !isOnline;

  return (
    <div className="chat-interface" aria-busy={isLoading}>
      <div className="chat-interface__messages">
        <MessageList ref={messageListRef} messages={messages} />
        <StreamingIndicator isVisible={isStreaming} />

        {error && error.retryable && (
          <div className="chat-interface__retry">
            <RetryButton onClick={retry} disabled={isLoading} />
          </div>
        )}

        {error && !error.retryable && (
          <div className="chat-interface__final-error" role="alert">
            {error.message}
          </div>
        )}

        {!isOnline && (
          <div className="chat-interface__offline-notice" role="alert">
            You appear to be offline. Check your connection and try again.
          </div>
        )}

        {showScrollButton && (
          <button
            type="button"
            className="chat-interface__scroll-button"
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
          >
            ↓
          </button>
        )}
      </div>

      <MessageInput
        onSubmit={submitMessage}
        disabled={inputDisabled}
        initialValue={draftMessage}
      />
    </div>
  );
}
